import type { JSX, ReactNode } from 'react';
import { NavLink, useLocation, useResolvedPath } from 'react-router-dom';

export type AppTabVariant = 'default' | 'admin';

export interface AppTabProps {
  /** Destination route. Relative paths resolve against the parent route. */
  to: string;
  /** Visible label. */
  label: string;
  /**
   * When true, only match the exact route — prevents a parent tab from
   * staying "active" on deep children. SurfTime Home uses `end` so `/surftime`
   * is only active on exactly that path, not `/surftime/map`.
   */
  end?: boolean;
  /**
   * Hide until the URL matches one of these patterns (string prefix match).
   * Used for contextual tabs like SurfTime's Beach detail which should only
   * appear when the user is on a `/surftime/beach/:slug` route.
   */
  revealOn?: string[];
  /** Optional leading icon. */
  icon?: ReactNode;
  /** Optional trailing count badge. */
  count?: number;
  /**
   * Visual variant (spec 015 US8).
   *   - 'default' — regular pill styled by the app's theme.
   *   - 'admin'   — same refined pill with an accent-tinted trailing dot
   *                 so app admins can spot their privileged surface at a
   *                 glance without breaking the unified tab treatment.
   */
  variant?: AppTabVariant;
}

/**
 * Per-app tab link.
 *
 * Uses `NavLink` so active state comes from the URL — this satisfies
 * FR-026 (deep-linkable, browser back/forward). A `revealOn` list allows a
 * tab to only surface on specific detail routes (see SurfTime Beach detail).
 */
export function AppTab({
  to,
  label,
  end,
  revealOn,
  icon,
  count,
  variant = 'default',
}: AppTabProps): JSX.Element | null {
  const { pathname } = useLocation();
  const resolved = useResolvedPath(to);

  if (revealOn && revealOn.length > 0) {
    const match = revealOn.some((prefix) => pathname.startsWith(prefix));
    if (!match) return null;
  }

  return (
    <NavLink
      to={to}
      end={end}
      role="tab"
      aria-selected={pathname === resolved.pathname}
      data-variant={variant}
      className={({ isActive }) =>
        [
          'app-tab',
          isActive ? 'app-tab--active' : '',
          variant === 'admin' ? 'app-tab--admin' : '',
        ]
          .filter(Boolean)
          .join(' ')
      }
    >
      {icon}
      <span>{label}</span>
      {typeof count === 'number' && count > 0 && (
        <span className="app-tab__count">{count}</span>
      )}
      {variant === 'admin' && <span className="app-tab__admin-dot" aria-hidden="true" />}
    </NavLink>
  );
}

export default AppTab;
